import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { callApi, HttpMethod } from "../../services/apiService";

export default function SetTermDate() {
  const [searchParams] = useSearchParams();
  const [acctId, setAcctId] = useState(searchParams.get("acctId") || "");
  const [accountName, setAccountName] = useState("");
  const [currentTermDate, setCurrentTermDate] = useState<string | null>(null);
  const [newTermDate, setNewTermDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(
    null,
  );

  useEffect(() => {
    if (searchParams.get("acctId")) {
      loadAccount(searchParams.get("acctId")!);
    }
  }, []);

  async function loadAccount(id: string) {
    setMessage(null);
    setLoading(true);
    try {
      const res = await callApi(
        `sysadmin/accounts/GetTermDate?acctId=${encodeURIComponent(id)}`,
        HttpMethod.GET,
      );
      setAccountName(res.name);
      setCurrentTermDate(
        res.lastDateOfTerm ? res.lastDateOfTerm.substring(0, 10) : null,
      );
      setNewTermDate(
        res.lastDateOfTerm ? res.lastDateOfTerm.substring(0, 10) : "",
      );
      setAcctId(res.acctId);
    } catch {
      setMessage({ type: "danger", text: `Account "${id}" not found.` });
      setAccountName("");
      setCurrentTermDate(null);
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setMessage(null);
    setLoading(true);
    try {
      const body = {
        acctId,
        lastDateOfTerm: newTermDate || null,
      };
      const res = await callApi(
        "sysadmin/accounts/SetTermDate",
        HttpMethod.POST,
        body,
      );
      setMessage({ type: "success", text: res.message });
      setCurrentTermDate(newTermDate || null);
    } catch {
      setMessage({ type: "danger", text: "Failed to update term date." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-3">
      {message && (
        <div
          className={`alert alert-${message.type} alert-dismissible`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
          />
        </div>
      )}

      <div className="row mb-3 align-items-end">
        <div className="col-auto">
          <label className="form-label fw-bold">Account ID</label>
          <input
            className="form-control"
            value={acctId}
            onChange={(e) => setAcctId(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && loadAccount(acctId)}
            placeholder="e.g. DEMO"
          />
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary"
            onClick={() => loadAccount(acctId)}
            disabled={!acctId || loading}
          >
            Load
          </button>
        </div>
      </div>

      {accountName && (
        <>
          <table className="table table-bordered" style={{ maxWidth: 500 }}>
            <tbody>
              <tr>
                <th>Account</th>
                <td>
                  {accountName} ({acctId})
                </td>
              </tr>
              <tr>
                <th>Current Term Date</th>
                <td>
                  {currentTermDate || (
                    <span className="text-muted">Not set</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="row mb-3 align-items-center">
            <div className="col-auto">
              <label className="form-label fw-bold mb-1">New Term Date</label>
              <input
                type="date"
                className="form-control"
                value={newTermDate}
                onChange={(e) => setNewTermDate(e.target.value)}
              />
            </div>
            <div className="col-auto d-flex gap-2" style={{ paddingTop: 24 }}>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setNewTermDate("")}
              >
                Clear
              </button>
              <button
                className="btn btn-primary"
                onClick={save}
                disabled={loading}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
